import { z } from "zod";
import { EmailDeliverySchema } from "./emailDelivery";

// Company documents: the paperwork customers ask a contractor for (W-9,
// certificates of insurance, licenses, bonds). The office keeps one library
// (Setup → Company documents) and sends any of them from a customer, project
// or job. Every send is logged with exactly which file version went to whom.
// Both collections are written by functions only.

export const COMPANY_DOC_CATEGORIES = [
  { id: "w9", label: "W-9", expires: false },
  { id: "insurance", label: "Certificate of insurance", expires: true },
  { id: "workers_comp", label: "Workers' comp certificate", expires: true },
  { id: "license", label: "License", expires: true },
  { id: "bond", label: "Bond", expires: true },
  { id: "other", label: "Other", expires: false },
] as const;
export const CompanyDocCategorySchema = z.enum(["w9", "insurance", "workers_comp", "license", "bond", "other"]);
export type CompanyDocCategory = z.infer<typeof CompanyDocCategorySchema>;

/** Largest single file (Storage rules enforce the same). */
export const COMPANY_DOC_MAX_BYTES = 20 * 1024 * 1024;
/** Largest total per email (the email provider's limit, after encoding, is 40 MB). */
export const COMPANY_DOC_SEND_MAX_BYTES = 25 * 1024 * 1024;

export const CompanyDocFileSchema = z.object({
  version: z.number().int().min(1),
  fileName: z.string(),
  storagePath: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().min(0),
  uploadedAt: z.number(),
  uploadedByName: z.string().default(""),
});
export type CompanyDocFile = z.infer<typeof CompanyDocFileSchema>;

export const CompanyDocSchema = z.object({
  name: z.string().min(1).max(120),
  category: CompanyDocCategorySchema,
  /** Shared-drive folder, e.g. "LiftMaster brochures" ("" = no folder). */
  folder: z.string().max(60).default(""),
  notes: z.string().max(1000).default(""),
  /** Certificates and licenses: the date it runs out (start of that day, ms). */
  expiresAt: z.number().nullable().default(null),
  /** Reminder emails already sent for this expiry date ("30", "7", "expired"); a new date starts over. */
  expiryReminder: z.object({ for: z.number(), stages: z.array(z.string()) }).nullable().optional(),
  /** Made out to one customer (a COI naming a builder as holder); null = anyone. */
  forCustomerId: z.string().nullable().default(null),
  forCustomerName: z.string().default(""),
  /** The current file. */
  file: CompanyDocFileSchema,
  /** Earlier files, newest first: kept so the send log can always show what went out. */
  previousFiles: z.array(CompanyDocFileSchema).default([]),
  sendCount: z.number().int().default(0),
  lastSentAt: z.number().nullable().default(null),
  archivedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
  updatedByName: z.string().default(""),
});
export type CompanyDoc = z.infer<typeof CompanyDocSchema>;

/** One email of company documents, as it was sent. */
export const DocSendSchema = z.object({
  docs: z.array(
    z.object({
      docId: z.string(),
      name: z.string(),
      category: CompanyDocCategorySchema,
      fileName: z.string(),
      version: z.number().int(),
      storagePath: z.string(),
      sizeBytes: z.number().int(),
      expiresAt: z.number().nullable(),
    }),
  ),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  subject: z.string(),
  message: z.string(),
  customerId: z.string().nullable(),
  customerName: z.string(),
  projectId: z.string().nullable(),
  projectName: z.string(),
  jobId: z.string().nullable(),
  jobTitle: z.string(),
  sentByUid: z.string(),
  sentByName: z.string(),
  sentByEmail: z.string(),
  sentAt: z.number(),
  status: z.enum(["sent", "failed"]),
  /** The email provider's id for the message (for tracing a delivery). */
  providerId: z.string().nullable(),
  /** What happened after it left (delivered, opened, bounced…). */
  delivery: EmailDeliverySchema.nullable().optional(),
  error: z.string().default(""),
  /** Someone chose to send a document past its expiration date. */
  expiredIncluded: z.boolean().default(false),
  /** Templates sent with it, filled for this customer: attached as a PDF, or a link to review and sign. */
  templates: z
    .array(
      z.object({
        templateId: z.string(),
        name: z.string(),
        sign: z.boolean(),
        agreementId: z.string().nullable(),
        /** What was sent, filled in (attached ones; signed ones live on their agreement). */
        body: z.string().max(30000).optional(),
      }),
    )
    .optional(),
  /** Sent again from an earlier send. */
  resentFrom: z.string().optional(),
});
export type DocSend = z.infer<typeof DocSendSchema>;

/** tenants/{t}/documentSets/{id}: a named bundle of company documents, attached by default where the company says. */
export const DocumentSetSchema = z.object({
  name: z.string().trim().min(1).max(80),
  docIds: z.array(z.string()).max(10).default([]),
  useFor: z
    .object({
      estimates: z.boolean().default(false),
      invoices: z.boolean().default(false),
      agreements: z.boolean().default(false),
    })
    .default({}),
  /** Estimates only: just these estimate types (empty = every type). */
  estimateTypeIds: z.array(z.string()).max(20).default([]),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type DocumentSet = z.infer<typeof DocumentSetSchema>;
